import React, { useState, useEffect } from 'react';
import { X, RotateCcw, ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';
import { CollectionItem, CaptchaData } from '../../types';
import { collectionService } from '../../services/collectionService';
import { secretKeyService } from '../../services/secretKeyService';
import { useToast } from '../../context/ToastContext';

interface RollbackModalProps {
  collection: CollectionItem;
  onClose: () => void;
  onSuccess: (lastUpdatedAt: string) => void;
}

export const RollbackModal: React.FC<RollbackModalProps> = ({
  collection,
  onClose,
  onSuccess,
}) => {
  const toast = useToast();

  const [captcha, setCaptcha] = useState<CaptchaData | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [loadingCaptcha, setLoadingCaptcha] = useState(false);

  const [rollingBack, setRollingBack] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const fetchCaptcha = async () => {
    setLoadingCaptcha(true);
    try {
      const data = await collectionService.generateCaptcha();
      setCaptcha(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingCaptcha(false);
    }
  };

  const handleRollback = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!captchaAnswer.trim()) {
      setError('Vui lòng nhập câu trả lời CAPTCHA.');
      return;
    }

    setRollingBack(true);

    try {
      const recoveryCode = secretKeyService.getUserRecoveryCode();
      const secretKeyHash = await secretKeyService.hashKey(recoveryCode);

      await collectionService.rollbackCollection({
        collectionId: collection.id,
        secretKeyHash,
        captcha: {
          nonce: captcha?.nonce || '',
          questionId: captcha?.questionId || '',
          answer: captchaAnswer.trim(),
        },
      });

      toast.success(`Đã khôi phục Bộ Sưu Tập "${collection.name}" về bản sao lưu trước thành công!`);
      onSuccess(new Date().toISOString());
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Khôi phục về bản cũ thất bại.');
      toast.error(err.message || 'Khôi phục về bản cũ thất bại.');
      fetchCaptcha();
      setCaptchaAnswer('');
    } finally {
      setRollingBack(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '1rem',
          width: '100%',
          maxWidth: '460px',
          padding: '1.25rem',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          position: 'relative',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ padding: '0.375rem', borderRadius: '0.375rem', backgroundColor: '#fef3c7', color: '#d97706', display: 'flex' }}>
              <RotateCcw size={18} />
            </div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
              Khôi Phục Bản Cũ
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={rollingBack}
            style={{
              padding: '0.375rem',
              borderRadius: '0.375rem',
              backgroundColor: '#f1f5f9',
              color: '#64748b',
              border: 'none',
              cursor: rollingBack ? 'not-allowed' : 'pointer',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Warning Body */}
        <div
          style={{
            backgroundColor: '#fffbe6',
            borderRadius: '0.5rem',
            padding: '0.875rem 1rem',
            marginBottom: '1.25rem',
            fontSize: '0.8125rem',
            color: '#856404',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.625rem',
          }}
        >
          <AlertTriangle size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: '0.125rem' }} />
          <div>
            Bộ sưu tập <strong>"{collection.name}"</strong> sẽ được khôi phục về bản sao lưu liền trước.
          </div>
        </div>

        {error && (
          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.375rem', padding: '0.625rem', marginBottom: '1rem', color: '#991b1b', fontSize: '0.8125rem' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleRollback}>
          {/* CAPTCHA Section */}
          <div
            style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '0.5rem',
              padding: '0.875rem 1rem',
              marginBottom: '1.25rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem', color: '#0369a1', fontWeight: 700, fontSize: '0.8125rem' }}>
              <ShieldCheck size={16} />
              Mã xác nhận (CAPTCHA)
            </div>
            {loadingCaptcha ? (
              <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>Đang tải CAPTCHA...</p>
            ) : captcha ? (
              <div>
                <p style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.375rem' }}>
                  {captcha.questionText}
                </p>
                <input
                  type="text"
                  placeholder="Nhập câu trả lời..."
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.4rem 0.625rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #93c5fd',
                    fontSize: '0.8125rem',
                    backgroundColor: '#ffffff',
                  }}
                />
              </div>
            ) : (
              <p style={{ fontSize: '0.75rem', color: 'red' }}>Không thể tải CAPTCHA.</p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={rollingBack}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                backgroundColor: '#f1f5f9',
                color: '#475569',
                fontWeight: 600,
                fontSize: '0.875rem',
                border: 'none',
                cursor: rollingBack ? 'not-allowed' : 'pointer',
              }}
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={rollingBack}
              style={{
                padding: '0.5rem 1.25rem',
                borderRadius: '0.375rem',
                backgroundColor: 'var(--primary-action)',
                color: '#0f172a',
                fontWeight: 700,
                fontSize: '0.875rem',
                border: 'none',
                cursor: rollingBack ? 'wait' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.375rem',
              }}
            >
              {rollingBack ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  <span>Đang khôi phục...</span>
                </>
              ) : (
                <>
                  <RotateCcw size={16} />
                  <span>Khôi phục</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
